/**
 * Refine Wording selection guard (S1-RW-06).
 *
 * The refine service returns PLAIN prose, so re-inserting the result can only
 * faithfully preserve a single, uniform block of text. A "bulk" selection —
 * one that spans a table/list, or mixes headings with body text (or several
 * heading levels) — gets flattened when the refined text is re-inserted:
 * headings lose their style, tables/lists are destroyed, and per-block
 * formatting is homogenised to the first block's attrs.
 *
 * Rather than silently damage the document, `handleRefine` calls this to
 * detect those unsupported selections and refuse gracefully (clear message,
 * NO changes). Uniform prose — a single block, or several body paragraphs of
 * the same style (the real expert-commentary case) — stays supported.
 */
import { type Node as PMNode, type Schema } from "prosemirror-model";

import { looksLikeVisualHeading } from "./para-clone";

export type RefineSelectionCheck =
  | { ok: true }
  | { ok: false; reason: "structure" | "mixed" };

// Word heading/title paragraph styles carry these in `styleId`; inline-styled
// section headings are caught separately via `looksLikeVisualHeading`.
const HEADING_STYLE_RE = /heading|title/i;

/** A paragraph that would read as a heading — by Word style, outline level, or
 *  the ALLCAPS+all-bold visual convention most MEDirect templates use. */
const isHeadingBlock = (node: PMNode): boolean => {
  const attrs = node.attrs as Record<string, unknown>;
  const styleId = String(attrs.styleId ?? "");
  if (HEADING_STYLE_RE.test(styleId)) return true;
  const outline = attrs.outlineLevel;
  if (outline !== null && outline !== undefined && outline !== "") return true;
  return looksLikeVisualHeading(node.textContent.trim(), node);
};

/** Structural fingerprint of a paragraph — two blocks with different style /
 *  outline can't be refined together without homogenising them. */
const blockSignature = (node: PMNode): string => {
  const attrs = node.attrs as Record<string, unknown>;
  return `${String(attrs.styleId ?? "")}|${String(attrs.outlineLevel ?? "")}`;
};

/**
 * Classify the selection `[from, to]` for Refine Wording.
 *
 * - `structure` — the range touches a table or list; a plain-text refine would
 *   destroy it.
 * - `mixed` — the range spans multiple blocks that aren't uniform body prose
 *   (a heading is present, or the paragraphs have differing styles); refining
 *   would flatten them to the first block's formatting.
 * - `ok` — a single block, or several same-style non-heading paragraphs.
 */
export const checkRefineSelection = (
  doc: PMNode,
  from: number,
  to: number
): RefineSelectionCheck => {
  const blocks: PMNode[] = [];
  let structural = false;

  doc.nodesBetween(from, to, (node) => {
    if (structural) return false;
    const name = node.type.name;
    // Tables and lists (and their structural children) can't survive a
    // plain-text refine — refuse the whole selection.
    if (/table|list/i.test(name)) {
      structural = true;
      return false;
    }
    if (node.isTextblock) {
      blocks.push(node);
      return false; // inline content doesn't affect the classification
    }
    return true;
  });

  if (structural) return { ok: false, reason: "structure" };
  // A single block always refines safely — it keeps its own attrs/marks.
  if (blocks.length <= 1) return { ok: true };
  // Multiple blocks are safe only if they're all the same non-heading style.
  const signatures = new Set(blocks.map(blockSignature));
  if (signatures.size > 1 || blocks.some(isHeadingBlock)) {
    return { ok: false, reason: "mixed" };
  }
  return { ok: true };
};

/** User-facing message for an unsupported Refine Wording selection. */
export const refineSelectionMessage = (reason: "structure" | "mixed"): string =>
  reason === "structure"
    ? "Refine Wording can't be applied to tables or lists. Select plain text within a single section."
    : "Refine Wording works on one block of text at a time — your selection spans headings or multiple sections. Select within a single paragraph or section.";

/** The visual attrs + emptiness of one original paragraph in the refined range. */
export type OriginalBlock = {
  attrs: Record<string, unknown> | null;
  isEmpty: boolean;
};

/**
 * Split the service's refined text back into paragraphs (S1-RW-06). Mirrors
 * `buildBlockNodes`: blank-line boundaries first, falling back to single
 * newlines when the service uses those between paragraphs. Empty blocks are
 * dropped — the paragraph *separation* is restored from the original structure,
 * not from the refined text, so a collapsed blank line here doesn't matter.
 */
export const splitRefinedParagraphs = (refined: string): string[] => {
  const clean = (b: string) => b.replace(/^\n+/, "").replace(/\n+$/, "");
  const nonEmpty = (b: string) => b.trim().length > 0;
  let blocks = refined
    .split(/\n{2,}/)
    .map(clean)
    .filter(nonEmpty);
  if (blocks.length <= 1) {
    const single = refined.split(/\n/).map(clean).filter(nonEmpty);
    if (single.length > blocks.length) blocks = single;
  }
  return blocks;
};

/**
 * Rebuild the refined text as paragraph nodes that MIRROR the original block
 * structure (S1-RW-06): each original *content* paragraph keeps its own visual
 * attrs and receives the next refined paragraph's text, while blank separator
 * paragraphs are re-emitted so the inter-paragraph spacing survives. This is
 * what stops a multi-paragraph refine from collapsing paragraphs together.
 *
 * Returns `null` when the refined paragraph count doesn't match the original
 * content-paragraph count (the service merged or split paragraphs) — the caller
 * then refuses rather than guessing a mapping and damaging the document.
 */
export const buildStructuredRefine = (
  schema: Schema,
  originalBlocks: readonly OriginalBlock[],
  refinedParagraphs: readonly string[],
  buildInline: (text: string) => PMNode[]
): PMNode[] | null => {
  const paragraphType = schema.nodes.paragraph ?? schema.nodes.doc_paragraph;
  if (!paragraphType) return null;
  const contentCount = originalBlocks.filter((b) => !b.isEmpty).length;
  if (contentCount === 0 || contentCount !== refinedParagraphs.length) return null;

  const out: PMNode[] = [];
  let i = 0;
  for (const block of originalBlocks) {
    if (block.isEmpty) {
      // Preserve the blank separator paragraph (and its attrs) → keeps the gap.
      out.push(paragraphType.create(block.attrs ?? null));
    } else {
      // `buildInline` turns the refined paragraph text into inline nodes —
      // re-applying inline emphasis + the destination font marks.
      out.push(
        paragraphType.create(
          block.attrs ?? null,
          buildInline(refinedParagraphs[i++])
        )
      );
    }
  }
  return out;
};
